import { FormControl, FormGroup } from "@angular/forms";
import { AssessmentPdfGenerator } from "src/app/patients/dashboard-page/assessments-history-tab/assessment-to-pdf";
import { ControlsOf, AssessmentHelpers } from "./shared";

interface CardiovascularFields {
  /**
   * Whether the patient's pulse pattern is irregular (true) or not (false).
   */
  pulsePattern: boolean;

  /**
   * The patient's heart tones.
   *
   * Options: murmur or gallup.
   */
  heartTones: string;

  /**
   * The patient's heart rhythm. If the nurse selects "other", they should be able to enter
   * a free form text input into the rhythm field.
   *
   * Options: atrial fibrillation, PVC's, PAC's, SVT, atrial flutter, or other - describe.
   */
  rhythm: string;
  rhythmOther: string;

  // Edema

  /**
   * Whether there is the presence of JVD (true) or not (false).
   */
  jvd: boolean;

  /**
   * Whether there is the presence of generalized non-pitting edema (true) or not (false).
   */
  gnpe: boolean;

  /**
   * The level of pitting edema in the patient's right hand.
   *
   * Options: 1+, 2+, 3+, or 4+.
   */
  rightHand: string;

  /**
   * The level of pitting edema in the patient's right foot.
   *
   * Options: 1+, 2+, 3+, or 4+.
   */
  rightFoot: string;

  /**
   * The level of pitting edema in the patient's left foot.
   *
   * Options: 1+, 2+, 3+, or 4+.
   */
  leftFoot: string;

  /**
   * The level of pitting edema in the patient's left hand.
   *
   * Options: 1+, 2+, 3+, or 4+.
   */
  leftHand: string;

  // Other

  /**
   * The patient's generalized perfusion.
   *
   * Options: cold, pale, cyanotic, or palpitations.
   */
  generalizedPerfusion: string;

  /**
   * Whether the patient's cap refill is greater than 3 seconds (true) or not (false).
   */
  capRefill: boolean;

  // Pulses

  /**
   * The strength of the pulse in the right upper extremity.
   *
   * Options: +3 strong; +2 normal; +1 weak, thready; or +0 absent.
   */
  pulseRue: string;

  /**
   * The strength of the pulse in the right lower extremity.
   *
   * Options: +3 strong; +2 normal; +1 weak, thready; or +0 absent.
   */
  pulseRle: string;

  /**
   * The strength of the pulse in the left upper extremity.
   *
   * Options: +3 strong; +2 normal; +1 weak, thready; or +0 absent.
   */
  pulseLue: string;

  /**
   * The strength of the pulse in the left lower extremity.
   *
   * Options: +3 strong; +2 normal; +1 weak, thready; or +0 absent.
   */
  pulseLle: string;

  cyanosisNoted: string;
  pacemaker: boolean;
  pacemakerComment: string;
}
export type CardiovascularFormGroup = FormGroup<ControlsOf<CardiovascularFields>>;

export const CardiovascularAssessmentHelpers: AssessmentHelpers = {
  createFormGroup: createCardiovascularFormGroup,
  toPDF: cardiovascularAssessmentToPDF,
  formToObject: formToCardiovascularAssessment,
  initialState: {
    pulsePattern: false, // is pulse pattern irregular?
    heartTones: '', // murmur, gallup
    rhythm: '', // NSR, atrial fibrillation, PVC's, PAC's, SVT, Atrial flutter, other
    otherRhythm: '', // for nurse to fill out if rhythm is "Other"
    jvd: false, // presence of JVD
    gnpe: false, // generalized non-pitting edema
    rightFoot: '',
    rightHand: '',
    leftFoot: '',
    leftHand: '',
    generalizedPerfusion: '', // cold, pale, cyanotic, palpitations
    capRefill: false, // is cap refill > 3 seconds?
    pulseRue: '', // +3 strong, +2 normal, +1 weak, thready, 0 absent
    pulseRle: '',
    pulseLue: '',
    pulseLle: '',
    cyanosisNoted: '',
    pacemaker: false,
    pacemakerComment: '',
  },
};

export class CardiovascularAssessment {
  pulsePattern: boolean;
  heartTones: string;
  rhythm: string;
  rhythmOther: string;
  jvd: boolean;
  gnpe: boolean;
  rightFoot: string;
  rightHand: string;
  leftFoot: string;
  leftHand: string;
  generalizedPerfusion: string;
  capRefill: boolean;
  pulseRue: string;
  pulseRle: string;
  pulseLue: string;
  pulseLle: string;
  cyanosisNoted: string;
  pacemaker: boolean;
  pacemakerComment: string;

  constructor(data: Partial<CardiovascularFields>) {
    const is = CardiovascularAssessmentHelpers.initialState;
    this.pulsePattern = data.pulsePattern ?? is.pulsePattern;
    this.heartTones = data.heartTones || is.heartTones;
    this.rhythm = data.rhythm || is.rhythm;
    this.rhythmOther = data.rhythmOther || is.rhythmOther;
    this.jvd = data.jvd ?? is.jvd;
    this.gnpe = data.gnpe ?? is.gnpe;
    this.rightFoot = data.rightFoot || is.rightFoot;
    this.rightHand = data.rightHand || is.rightHand;
    this.leftFoot = data.leftFoot || is.leftFoot;
    this.leftHand = data.leftHand || is.leftHand;
    this.generalizedPerfusion = data.generalizedPerfusion || is.generalizedPerfusion;
    this.capRefill = data.capRefill ?? is.capRefill;
    this.pulseRue = data.pulseRue || is.pulseRue;
    this.pulseRle = data.pulseRle || is.pulseRle;
    this.pulseLue = data.pulseLue || is.pulseLue;
    this.pulseLle = data.pulseLle || is.pulseLle;
    this.cyanosisNoted = data.cyanosisNoted || is.cyanosisNoted;;
    this.pacemaker = data.pacemaker ?? is.pacemaker;
    this.pacemakerComment = data.pacemakerComment || is.pacemakerComment;
  }
}

function createCardiovascularFormGroup(cardiovascularAssessment?: CardiovascularAssessment) {
  const data = cardiovascularAssessment || new CardiovascularAssessment(CardiovascularAssessmentHelpers.initialState);
  return new FormGroup<ControlsOf<CardiovascularFields>>({
    pulsePattern: new FormControl<boolean | null>(data.pulsePattern),
    heartTones: new FormControl<string | null>(data.heartTones),
    rhythm: new FormControl<string | null>(data.rhythm),
    rhythmOther: new FormControl<string | null>(data.rhythmOther),
    jvd: new FormControl<boolean | null>(data.jvd),
    gnpe: new FormControl<boolean | null>(data.gnpe),
    rightFoot: new FormControl<string | null>(data.rightFoot),
    rightHand: new FormControl<string | null>(data.rightHand),
    leftFoot: new FormControl<string | null>(data.leftFoot),
    leftHand: new FormControl<string | null>(data.leftHand),
    generalizedPerfusion: new FormControl<string | null>(data.generalizedPerfusion),
    capRefill: new FormControl<boolean | null>(data.capRefill),
    pulseRue: new FormControl<string | null>(data.pulseRue),
    pulseRle: new FormControl<string | null>(data.pulseRle),
    pulseLue: new FormControl<string | null>(data.pulseLue),
    pulseLle: new FormControl<string | null>(data.pulseLle),
    cyanosisNoted: new FormControl<string | null>(data.cyanosisNoted),
    pacemaker: new FormControl<boolean | null>(data.pacemaker),
    pacemakerComment: new FormControl<string | null>(data.pacemakerComment),
  });
}

function cardiovascularAssessmentToPDF(pdf: AssessmentPdfGenerator, assessment: CardiovascularAssessment) {
  pdf.addSectionTitle("Cardiovascular");
  pdf.pdf.text("Heart Tones", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addTwoColumnText("Irregular Pulse", assessment.pulsePattern ? "Yes" : "No",
    "Heart Tones", assessment.heartTones);
  pdf.addTwoColumnText("Rhythm", assessment.rhythm, "", "");
  pdf.currY += 10;
  pdf.pdf.text("Edema", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addTwoColumnText("Presence of JVD", assessment.jvd ? "Yes" : "No",
    "Generalized Non-Pitting Edema", assessment.gnpe ? "Yes" : "No");
  pdf.currY += 10;
  pdf.pdf.text("Pitting Edema", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addTwoColumnText("Left Hand", assessment.leftHand, "Right Hand", assessment.rightHand);
  pdf.addTwoColumnText("Left Foot", assessment.leftFoot, "Right Foot", assessment.rightFoot);
  pdf.addTwoColumnText("Generalized Perfusion", assessment.generalizedPerfusion,
    "Cap Refill", assessment.capRefill ? "Yes" : "No");
  pdf.currY += 10;
  pdf.pdf.text("Pulses", pdf.margin, pdf.currY)
  pdf.currY += 20;
  pdf.addTwoColumnText("RUE", assessment.pulseRue, "RLE", assessment.pulseRle);
  pdf.addTwoColumnText("LUE", assessment.pulseLue, "LLE", assessment.pulseLle);
  pdf.currY += 10;
  pdf.pdf.text("Other", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addTwoColumnText("Cyanosis", assessment.cyanosisNoted,
    "Pacemaker", assessment.pacemaker ? "Yes" : "No");
  pdf.addOneColumnText("Pacemaker Comment", assessment.pacemakerComment);
}

function formToCardiovascularAssessment(fg: CardiovascularFormGroup) {

  let rhythm = fg.controls.rhythm?.value;

  // Append other text to form values that are "Other"
  if (rhythm === 'Other') {
    rhythm = rhythm + ' - ' + fg.controls.rhythmOther?.value;
  }

  return new CardiovascularAssessment({
    pulsePattern: fg.controls.pulsePattern.value!,
    heartTones: fg.controls.heartTones.value!,
    rhythm: rhythm!,
    rhythmOther: fg.controls.rhythmOther.value!,
    jvd: fg.controls.jvd.value!,
    gnpe: fg.controls.gnpe.value!,
    rightHand: fg.controls.rightHand.value!,
    rightFoot: fg.controls.rightFoot.value!,
    leftFoot: fg.controls.leftFoot.value!,
    leftHand: fg.controls.leftHand.value!,
    generalizedPerfusion: fg.controls.generalizedPerfusion.value!,
    capRefill: fg.controls.capRefill.value!,
    pulseRue: fg.controls.pulseRue.value!,
    pulseRle: fg.controls.pulseRle.value!,
    pulseLue: fg.controls.pulseLue.value!,
    pulseLle: fg.controls.pulseLle.value!,
    cyanosisNoted: fg.controls.cyanosisNoted.value!,
    pacemaker: fg.controls.pacemaker.value!,
    pacemakerComment: fg.controls.pacemakerComment.value!,
  });
}